<template>
  <div class="review-answers">
    <div class="container mt-5">
      <h2 class="text-center mb-4">Review Answers for Current Active Round</h2>

      <div class="d-flex justify-content-center mb-4">
        <button @click="reviewOtherRounds" class="btn btn-warning me-2">Review Previous Rounds</button>
        <button v-if="activeRound && activeRound.roundName === 'Wishbone Split'" @click="findClosestPercentageGuess" class="btn btn-info me-2">
          Find Closest Percentage Guess
        </button>
        <button @click="submitAllScores" class="btn btn-success me-2" :disabled="!hasUnsavedScores || activeRound.roundName === 'Bonus Points Survey'">
          Submit All Scores
        </button>
      </div>

      <!-- Teams Table -->
      <div v-if="teams.length > 0" class="mt-4">
        <table class="table table-striped table-hover">
          <thead class="table-dark">
            <tr>
              <th>Team Name</th>
              <th>Points this Round</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="team in teams" :key="team.id">
              <td>
                {{ team.teamName }}
                <span 
                  :class="[
                    'badge ms-2',
                    getTeamSubmissionStatus(team.id).color === 'green' ? 'bg-success' :
                    getTeamSubmissionStatus(team.id).color === 'yellow' ? 'bg-warning' : 'bg-danger'
                  ]"
                  :title="`Submitted ${getTeamSubmissionStatus(team.id).submittedCount} out of ${getTeamSubmissionStatus(team.id).currentRoundNumber} rounds`"
                >
                  {{ getTeamSubmissionStatus(team.id).text }}
                </span>
              </td>
              <td class="text-end">
                {{ team.submission?.totalScore || 0 }}
              </td>
              <td>
                <button v-if="!team.hasSubmitted" class="btn btn-secondary btn-sm" disabled>
                  No Answers Submitted Yet
                </button>
                <div v-else class="d-flex align-items-center">
                  <button 
                    :class="[
                      'btn btn-sm',
                      team.hasBeenScored ? 'btn-info' : 'btn-success'
                    ]"
                    @click="scoreRound(team)"
                  >
                    {{ team.hasBeenScored ? 'Round Scored - Click to Edit' : 'Score Round' }}
                  </button>
                  <div v-if="pendingAutoScoring.has(team.id)" class="spinner-border spinner-border-sm ms-2">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <span v-else-if="team.submission?.preScored" class="robot-emoji ms-2" role="img" aria-label="Robot">🤖</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div v-else class="alert alert-info">
        <p>No teams have submitted answers yet.</p>
      </div>
    </div>

    <!-- Scoring Modal -->
    <div v-if="showScoringModal && selectedTeam" class="modal fade show" tabindex="-1">
      <div class="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Score Round: {{ selectedReviewRoundId ? reviewRound?.roundName : activeRound?.roundName }} 
              for {{ selectedTeam.teamName }}
            </h5>
            <button type="button" class="btn-close" @click="closeScoringModal"></button>
          </div>
          <div class="modal-body">
            <!-- Loading Overlay -->
            <div v-if="isAutoScoring" class="auto-scoring-overlay">
              <div class="robot-emoji">🤖</div>
              <div class="scoring-text">Auto-scoring in progress...</div>
            </div>

            <!-- Answer Key Table -->
            <h6>Correct Answers</h6>
            <table class="table table-bordered table-sm">
              <thead class="table-light">
                <tr>
                  <th>Question</th>
                  <th>Correct Answer</th>
                  <th>Point Value</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(question, index) in currentRoundQuestions" :key="index">
                  <td>{{ question?.text || `Question ${index + 1}` }}</td>
                  <td>
                    {{ question?.type === 'wishbone' 
                      ? question.options[question.correctOptionIndex] 
                      : question?.answer 
                    }}
                  </td>
                  <td>{{ question?.pointValue }}</td>
                </tr>
              </tbody>
            </table>

            <!-- Team's Answers Table -->
            <h6 class="mt-4">Team's Answers and Scoring</h6>
            <table class="table table-bordered table-sm" v-if="selectedTeam && selectedTeam.submission">
              <thead class="table-light">
                <tr>
                  <th>Question</th>
                  <th>Team's Answer</th>
                  <th>Assign Points</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="(answer, index) in selectedTeam.submission.answers" 
                  :key="index"
                  :class="{
                    'auto-scored': localScores[selectedTeam.id]?.autoScored[index],
                    'incorrect': localScores[selectedTeam.id]?.scores[index] === 0
                  }"
                >
                  <td>Question {{ index + 1 }}</td>
                  <td>{{ answer }}</td>
                  <td>
                    <input 
                      type="number" 
                      :value="getTeamScore(index)"
                      @input="updateTeamScore($event, index)"
                      class="form-control form-control-sm" 
                      required 
                      min="0"
                      :max="currentRoundQuestions[index]?.pointValue || 100"
                    >
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="2" class="text-end"><strong>Total Score:</strong></td>
                  <td>{{ getTotalScore() }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeScoringModal">Close</button>
            <button type="button" class="btn btn-primary" @click="submitScores">Save Scores</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Round Selector Modal -->
    <div v-if="showRoundSelector" class="modal fade show" tabindex="-1" @click.self="closeRoundSelector">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Select Round to Review</h5>
            <button type="button" class="btn-close" @click="closeRoundSelector"></button>
          </div>
          <div class="modal-body">
            <label for="selectRoundToReview" class="form-label">Select Round:</label>
            <select id="selectRoundToReview" v-model="selectedReviewRoundId" class="form-select">
              <option v-for="round in rounds" :key="round.id" :value="round.id">
                {{ round.roundName }}
              </option>
            </select>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" @click="reviewSelectedRound(selectedReviewRoundId)">Review Round</button>
            <button type="button" class="btn btn-secondary" @click="closeRoundSelector">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Closest Percentage Guess Modal -->
    <div v-if="showClosestGuessModal" class="modal fade show" tabindex="-1">
      <div class="modal-dialog modal-dialog-scrollable modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Closest Percentage Guess</h5>
            <button type="button" class="btn-close" @click="closeClosestGuessModal"></button>
          </div>
          <div class="modal-body">
            <p><strong>Correct Wishbone Answer:</strong> {{ activeRound.questions[0].options[activeRound.questions[0].correctOptionIndex] }}</p>
            <p><strong>Correct Percentage:</strong> {{ activeRound.questions[1].answer }}%</p>
            <p><strong>Closest Guess(es) among teams with correct Wishbone answer:</strong></p>
            <ul>
              <li v-for="result in closestGuessResults" :key="result.teamId">
                {{ result.teamName }}: {{ result.guess }}% (Difference: {{ result.difference.toFixed(2) }}%)
                <button 
                  @click="applyBonusPoints(result.teamId, result.bonusPoints)" 
                  class="btn btn-sm" 
                  :class="result.bonusApplied ? 'btn-success' : 'btn-primary'"
                  :disabled="result.bonusApplied"
                >
                  {{ result.bonusApplied ? 'Bonus Applied' : `Apply ${result.bonusPoints} Bonus Points` }}
                </button>
              </li>
            </ul>
          </div>
          <div class="modal-footer">
            <button @click="closeClosestGuessModal" class="btn btn-secondary">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Backdrop -->
    <div v-if="showScoringModal || showRoundSelector || showClosestGuessModal" class="modal-backdrop fade show"></div>

    <!-- On-screen messages -->
    <div v-if="message" :class="['alert', messageType === 'success' ? 'alert-success' : 'alert-danger']">
      {{ message }}
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, computed, toRef } from 'vue';
import { db } from '@/firebase';
import { collection, query, where, onSnapshot, doc, getDocs, writeBatch, getDoc } from 'firebase/firestore';
import { autoScoreAnswers, AUTO_SCORING_ROUNDS } from '@/services/autoScoringService';

export default {
  name: 'ReviewAnswers',
  emits: ['update-active-round'],
  props: {
    selectedLocation: {
      type: String,
      required: true,
    },
    activeRound: {
      type: Object,
      required: true,
    },
    rounds: {
      type: Array,
      required: true,
    },
  },
  setup(props, { emit }) {
    const selectedRoundId = ref(props.activeRound.id);
    const selectedReviewRoundId = ref(null);
    const showRoundSelector = ref(false);
    const teams = ref([]);
    const selectedTeam = ref(null);
    const showScoringModal = ref(false);
    const showClosestGuessModal = ref(false);
    const closestGuessResults = ref([]);
    const isLoading = ref(false);
    const isAutoScoring = ref(false);
    const pendingAutoScoring = ref(new Set());
    let unsubscribeTeams = null;

    const activeRound = toRef(props, 'activeRound');
    const reviewRound = ref(null);

    // Add new reactive variables for team submission status
    const teamSubmissionStatus = ref({});

    // Function to calculate team submission status
    const calculateTeamSubmissionStatus = async (team) => {
      if (!team || !activeRound.value?.gameId) return;

      try {
        // Get all submissions for this team
        // Use team.teamId if available, otherwise fall back to team.id
        const teamIdToQuery = team.teamId || team.id;
        const submissionsQuery = query(
          collection(db, `games/${activeRound.value.gameId}/submissions`),
          where('teamId', '==', teamIdToQuery)
        );
        
        const submissionsSnapshot = await getDocs(submissionsQuery);
        const submittedRounds = new Set();
        
        submissionsSnapshot.forEach(doc => {
          const submission = doc.data();
          // Only count regular round submissions, not bonus submissions
          if (!submission.isBonus && !submission.isManualAdjustment) {
            // Check if this is Round 7 (intermission) and exclude it
            // Common intermission round names to exclude
            const intermissionRoundNames = [
              'Bonus Points Survey',
              'Intermission',
              'Break',
              'Pause'
            ];
            
            if (!intermissionRoundNames.includes(submission.roundName)) {
              submittedRounds.add(submission.roundId);
            }
          }
        });

        // No need for additional filtering since we already excluded Round 7 above
        const nonRound7Submissions = Array.from(submittedRounds);

        const submittedCount = nonRound7Submissions.length;
        const currentRoundNumber = activeRound.value.roundNumber;
        
        // Calculate expected submissions excluding intermission rounds
        // We need to determine how many intermission rounds have occurred so far
        // For now, we'll use a simple approach: if we're past round 7, subtract 1
        // This can be made more sophisticated later if needed
        const expectedSubmissions = currentRoundNumber <= 7 ? currentRoundNumber : currentRoundNumber - 1;
        
        // Determine color based on submission status
        let color = 'red';
        if (submittedCount >= expectedSubmissions - 1) {
          color = 'green';
        } else if (submittedCount >= expectedSubmissions - 2) {
          color = 'yellow';
        }

        teamSubmissionStatus.value[team.id] = {
          submittedCount,
          currentRoundNumber: expectedSubmissions,
          color,
          text: `${submittedCount}/${expectedSubmissions}`
        };
        
        console.log(`Team ${team.teamName} submission status:`, {
          teamId: team.teamId || team.id,
          submittedCount,
          expectedSubmissions,
          color,
          text: `${submittedCount}/${expectedSubmissions}`
        });
      } catch (error) {
        console.error('Error calculating submission status for team:', team.id, error);
      }
    };

    // Function to get submission status for a team
    const getTeamSubmissionStatus = (teamId) => {
      return teamSubmissionStatus.value[teamId] || {
        submittedCount: 0,
        currentRoundNumber: 0,
        color: 'red',
        text: '0/0'
      };
    };

    const fetchSubmissions = (roundId) => {
      if (!roundId || !activeRound.value || !activeRound.value.gameId) {
        console.error('Invalid round ID or game ID');
        return;
      }

      isLoading.value = true;
      teams.value = [];

      const teamsQuery = query(
        collection(db, 'teams'),
        where('gameId', '==', activeRound.value.gameId)
      );

      unsubscribeTeams = onSnapshot(teamsQuery, (teamsSnapshot) => {
        const updatedTeams = teamsSnapshot.docs.map((teamDoc) => {
          const teamData = teamDoc.data();
          const submissionRef = doc(
            db,
            'games',
            activeRound.value.gameId,
            'submissions',
            `${teamData.teamId}_${roundId}`
          );

          // Create initial team object
          const team = {
            id: teamDoc.id,
            teamId: teamData.teamId, // Include the teamId field
            teamName: teamData.teamName,
            submissionRef: submissionRef,
            hasSubmitted: false,
            hasBeenScored: false,
            submittedToFirestore: false,
            submission: { answers: [], scores: [] },
          };

          // Return both the team object and a promise for its submission
          return {
            team,
            submissionPromise: getDoc(submissionRef).then(docSnapshot => {
              if (docSnapshot.exists()) {
                const data = docSnapshot.data();
                return {
                  ...team,
                  submission: data,
                  hasSubmitted: true,
                  hasBeenScored: data.hasBeenScored || false,
                  submittedToFirestore: data.hasBeenScored || false,
                };
              }
              return team;
            })
          };
        });

        // Wait for all submission promises to resolve
        Promise.all(updatedTeams.map(({ submissionPromise }) => submissionPromise))
          .then(resolvedTeams => {
            teams.value = resolvedTeams;
            isLoading.value = false;

            // Calculate submission status for all teams
            resolvedTeams.forEach(team => {
              calculateTeamSubmissionStatus(team);
            });

            // Set up individual listeners for each submission
            resolvedTeams.forEach(team => {
              onSnapshot(team.submissionRef, (docSnapshot) => {
                const teamIndex = teams.value.findIndex(t => t.id === team.id);
                if (teamIndex !== -1) {
                  if (docSnapshot.exists()) {
                    const data = docSnapshot.data();
                    teams.value[teamIndex] = {
                      ...teams.value[teamIndex],
                      submission: data,
                      hasSubmitted: true,
                      hasBeenScored: data.hasBeenScored || false,
                      submittedToFirestore: data.hasBeenScored || false,
                    };
                  } else {
                    teams.value[teamIndex] = {
                      ...teams.value[teamIndex],
                      hasSubmitted: false,
                      hasBeenScored: false,
                      submittedToFirestore: false,
                    };
                  }
                  // Force reactivity update
                  teams.value = [...teams.value];
                  
                  // Recalculate submission status when submission changes
                  calculateTeamSubmissionStatus(teams.value[teamIndex]);
                }
              });
            });
          });
      }, (error) => {
        console.error("Error fetching submissions:", error);
        isLoading.value = false;
      });
    };

    const changeActiveRound = async () => {
      fetchSubmissions(selectedRoundId.value);
    };

    const reviewOtherRounds = async () => {
      showRoundSelector.value = true;
    };

    const reviewSelectedRound = async (roundId) => {
      showRoundSelector.value = false;
      selectedReviewRoundId.value = roundId;
      const selectedRound = props.rounds.find(round => round.id === roundId);
      
      if (selectedRound) {
        await fetchSubmissionsForRound(roundId, selectedRound);
      } else {
        console.error('Selected round not found');
      }
    };

    const fetchSubmissionsForRound = async (roundId, roundData) => {
      if (!roundId || !activeRound.value.gameId) {
        console.error('Invalid round ID or game ID');
        return;
      }

      isLoading.value = true;
      teams.value = [];

      const teamsQuery = query(
        collection(db, 'teams'),
        where('gameId', '==', activeRound.value.gameId)
      );

      const teamsSnapshot = await getDocs(teamsQuery);
      
      const updatedTeams = await Promise.all(teamsSnapshot.docs.map(async (teamDoc) => {
        const teamData = teamDoc.data();
        const submissionRef = doc(
          db,
          'games',
          activeRound.value.gameId,
          'submissions',
          `${teamData.teamId}_${roundId}`
        );

        const submissionSnapshot = await getDoc(submissionRef);

        const team = {
          id: teamDoc.id,
          teamId: teamData.teamId, // Include the teamId field
          teamName: teamData.teamName,
          submissionRef: submissionRef,
          hasSubmitted: false,
          hasBeenScored: false,
          submittedToFirestore: false,
          submission: { answers: [], scores: [] },
        };

        if (submissionSnapshot.exists()) {
          const data = submissionSnapshot.data();
          team.submission = data;
          team.hasSubmitted = true;
          team.hasBeenScored = data.hasBeenScored || false;
          team.submittedToFirestore = data.hasBeenScored || false;
        }

        return team;
      }));

      teams.value = updatedTeams;
      reviewRound.value = { ...roundData, id: roundId };
      isLoading.value = false;

      // Calculate submission status for all teams when reviewing other rounds
      updatedTeams.forEach(team => {
        calculateTeamSubmissionStatus(team);
      });
    };

    const closeRoundSelector = () => {
      showRoundSelector.value = false;
      selectedReviewRoundId.value = null;
      reviewRound.value = null;
      fetchSubmissions(activeRound.value.id);
    };

    const localScores = ref({});

    const hasUnsavedScores = computed(() => {
      if (props.activeRound.roundName === "Bonus Points Survey") {
        return true; // Always allow advancing for this round
      }
      return Object.keys(localScores.value).length > 0 || 
             teams.value.some(team => team.hasBeenScored && !team.submittedToFirestore);
    });

    const currentRoundQuestions = computed(() => {
      return selectedReviewRoundId.value ? reviewRound.value.questions : activeRound.value.questions;
    });

    const scoreTargetPractice = (submittedAnswer, correctAnswer, maxPoints) => {
      const submitted = parseFloat(submittedAnswer);
      const correct = parseFloat(correctAnswer);
      
      if (isNaN(submitted) || isNaN(correct)) return 0;
      
      const difference = Math.abs(submitted - correct);
      const percentageOff = (difference / correct) * 100;
      
      if (percentageOff <= 10) return maxPoints;
      if (percentageOff <= 20) return Math.floor(maxPoints * 0.9);
      if (percentageOff <= 30) return Math.floor(maxPoints * 0.8);
      if (percentageOff <= 50) return Math.floor(maxPoints * 0.6);
      if (percentageOff <= 75) return Math.floor(maxPoints * 0.4);
      return 0;
    };

    const triggerAutoScoring = async (team) => {
      if (!team || pendingAutoScoring.value.has(team.id)) return;

      try {
        pendingAutoScoring.value.add(team.id);
        
        console.log("🔍 Submitted Answers:", team.submission.answers);
        console.log("✅ Correct Answers:", currentRoundQuestions.value.map(q => q.answer));
        console.log("🔢 Full API Payload:", JSON.stringify({
          submittedAnswers: team.submission.answers,
          correctAnswers: currentRoundQuestions.value.map(q => q.answer)
        }, null, 2));

        const scoringResult = await autoScoreAnswers(
          team.submission.answers,
          currentRoundQuestions.value
        );

        if (scoringResult && Array.isArray(scoringResult.scores)) {
          console.log('Auto-scoring results:', scoringResult);
          
          // Update the team's submission
          team.submission = {
            ...team.submission,
            scores: [...scoringResult.scores],
            totalScore: scoringResult.totalScore,
            preScored: true,
            hasBeenAutoScored: true
          };

          // Update localScores
          localScores.value = {
            ...localScores.value,
            [team.id]: {
              scores: [...scoringResult.scores],
              autoScored: [...scoringResult.autoScored],
              totalScore: scoringResult.totalScore
            }
          };

          console.log('Updated team submission:', team.submission);
          
          // Force reactive updates
          teams.value = teams.value.map(t => 
            t.id === team.id ? {...t, submission: {...team.submission}} : t
          );
        }
      } catch (error) {
        console.error('Error in auto-scoring:', error);
      } finally {
        pendingAutoScoring.value.delete(team.id);
      }
    };

    const scoreRound = async (team) => {
      if (!team || !team.submission) return;

      console.log('Opening scoring modal for team:', team.id);
      
      const roundName = selectedReviewRoundId.value 
        ? reviewRound.value?.roundName 
        : activeRound.value?.roundName;

      // Initialize localScores if needed
      if (!localScores.value[team.id]) {
        if (roundName === 'Target Practice') {
          // Auto-calculate Target Practice scores
          const scores = team.submission.answers.map((answer, index) => {
            const question = currentRoundQuestions.value[index];
            return scoreTargetPractice(answer, question.answer, question.pointValue);
          });

          const totalScore = scores.reduce((sum, score) => sum + score, 0);

          localScores.value[team.id] = {
            scores: scores,
            autoScored: new Array(scores.length).fill(true),
            totalScore: totalScore
          };
        } else if (roundName === 'Wishbone Split') {
          // Auto-calculate Wishbone Split scores
          const scores = team.submission.answers.map((answer, index) => {
            const question = currentRoundQuestions.value[index];
            if (index === 0) { // First question - the wishbone choice
              // Check if their answer matches the correct option
              const correctAnswer = question.options[question.correctOptionIndex];
              return answer === correctAnswer ? question.pointValue : 0;
            }
            return 0; // Initialize second question score as 0
          });

          const totalScore = scores.reduce((sum, score) => sum + score, 0);

          localScores.value[team.id] = {
            scores: scores,
            autoScored: new Array(scores.length).fill(true),
            totalScore: totalScore
          };
        } else if (roundName === 'Popularity Contest') {
          // Auto-calculate Popularity Contest scores
          const scores = team.submission.answers.map((answer, index) => {
            const question = currentRoundQuestions.value[index];
            const correctAnswer = question.options[question.correctOptionIndex];
            const score = answer === correctAnswer ? question.pointValue : 0;
            console.log(`Question ${index + 1}:`, { answer, correctAnswer, score });
            return score;
          });

          const autoScored = team.submission.answers.map((answer, index) => {
            const question = currentRoundQuestions.value[index];
            const correctAnswer = question.options[question.correctOptionIndex];
            return answer === correctAnswer;
          });

          const totalScore = scores.reduce((sum, score) => sum + score, 0);
          console.log('Calculated scores:', scores);
          console.log('Auto-scored flags:', autoScored);
          console.log('Total score:', totalScore);

          localScores.value[team.id] = {
            scores: scores,
            autoScored: autoScored,
            totalScore: totalScore
          };
        } else {
          // Normal initialization for other rounds
          localScores.value[team.id] = {
            scores: [...(team.submission.scores || new Array(team.submission.answers.length).fill(0))],
            autoScored: new Array(team.submission.answers.length).fill(team.submission.preScored || false),
            totalScore: team.submission.totalScore || 0
          };
        }
      }

      console.log('LocalScores for team:', localScores.value[team.id]);
      selectedTeam.value = team;
      showScoringModal.value = true;
    };

    const submitScores = async () => {
      if (!selectedTeam.value) return;

      const teamId = selectedTeam.value.id;
      const teamScores = localScores.value[teamId];
      const roundId = selectedReviewRoundId.value || activeRound.value?.id;
      
      if (!roundId) {
        console.error('No round ID available');
        return;
      }
      
      try {
        // Update the team's submission in the teams array (local state only)
        // Do NOT write to Firestore here - that will happen when "Submit All Scores" is clicked
        const updatedTeam = teams.value.find(t => t.id === teamId);
        if (updatedTeam) {
          updatedTeam.submission = {
            ...updatedTeam.submission,
            scores: [...teamScores.scores],
            totalScore: teamScores.totalScore,
            submittedToFirestore: false, // Keep as false until "Submit All Scores" is clicked
            preScored: false
          };
          updatedTeam.hasBeenScored = true;
        }

        // Force a reactive update of the teams array
        teams.value = [...teams.value];

        showScoringModal.value = false;
        console.log('Scores saved to local state (will be written to Firestore when "Submit All Scores" is clicked)');
      } catch (error) {
        console.error('Error saving scores to local state:', error, {
          teamId,
          roundId,
          gameId: activeRound.value?.gameId
        });
      }
    };

    const closeScoringModal = () => {
      showScoringModal.value = false;
      selectedTeam.value = null;
    };

    const message = ref('');
    const messageType = ref('');

    const showMessage = (text, type = 'success') => {
      message.value = text;
      messageType.value = type;
      setTimeout(() => {
        message.value = '';
        messageType.value = '';
      }, 5000); // Message disappears after 5 seconds
    };

    const submitAllScores = async () => {
      try {
        const batch = writeBatch(db);
        const roundId = selectedReviewRoundId.value || activeRound.value.id;

        for (const team of teams.value) {
          if (team.hasBeenScored && !team.submission?.submittedToFirestore) {
            const submissionRef = doc(
              db, 
              'games', 
              activeRound.value.gameId, 
              'submissions', 
              `${team.id}_${roundId}`
            );
            
            // Read from localScores (source of truth) instead of team.submission
            // This ensures bonus points and other local changes are included
            const localScoreData = localScores.value[team.id];
            const scores = localScoreData?.scores || team.submission?.scores || [];
            const totalScore = localScoreData?.totalScore || team.submission?.totalScore || 0;
            
            batch.update(submissionRef, {
              scores,
              totalScore,
              submittedToFirestore: true,
              preScored: false,
              hasBeenScored: true
            });
          }
        }

        await batch.commit();
        showMessage('All scores submitted successfully!');
      } catch (error) {
        console.error('Error submitting all scores:', error);
        showMessage('Error submitting scores', 'error');
      }
    };

    const submitAllScoresAndAdvance = async () => {
      await submitAllScores();
      emit('update-active-round');
    };

    const findClosestPercentageGuess = () => {
      if (activeRound.value.roundName !== 'Wishbone Split') return;

      const correctWishboneAnswer = activeRound.value.questions[0].options[activeRound.value.questions[0].correctOptionIndex];
      const correctPercentage = parseFloat(activeRound.value.questions[1].answer);
      const bonusPoints = parseInt(activeRound.value.questions[1].pointValue) || 0;
      let smallestDifference = Infinity;
      let closestTeams = [];

      for (const team of teams.value) {
        if (team.hasSubmitted) {
          if (team.submission.answers[0] === correctWishboneAnswer) {
            const guess = parseFloat(team.submission.answers[1]);
            const difference = Math.abs(guess - correctPercentage);

            if (difference < smallestDifference) {
              smallestDifference = difference;
              closestTeams = [{ teamId: team.id, teamName: team.teamName, guess, difference }];
            } else if (difference === smallestDifference) {
              closestTeams.push({ teamId: team.id, teamName: team.teamName, guess, difference });
            }
          }
        }
      }

      closestGuessResults.value = closestTeams.map(team => ({
        ...team,
        bonusPoints,
        bonusApplied: false
      }));
      showClosestGuessModal.value = true;
    };

    const applyBonusPoints = (teamId, bonusPoints) => {
      const team = teams.value.find(t => t.id === teamId);
      if (team) {
        if (!localScores.value[teamId]) {
          localScores.value[teamId] = {
            scores: [...(team.submission?.scores || [])],
            totalScore: team.submission?.totalScore || 0
          };
        }
        // Add bonus points to the second question's score (index 1)
        localScores.value[teamId].scores[1] = (localScores.value[teamId].scores[1] || 0) + bonusPoints;
        // Recalculate total score based on all scores
        localScores.value[teamId].totalScore = localScores.value[teamId].scores.reduce((sum, score) => sum + (Number(score) || 0), 0);
        team.hasBeenScored = true;
        
        // Also update team.submission to keep it in sync (the watcher should handle this, but being explicit)
        if (team.submission) {
          team.submission = {
            ...team.submission,
            scores: [...localScores.value[teamId].scores],
            totalScore: localScores.value[teamId].totalScore
          };
        }
        
        // Force reactive update
        teams.value = [...teams.value];
        
        // Update closestGuessResults to reflect the applied bonus
        const resultIndex = closestGuessResults.value.findIndex(r => r.teamId === teamId);
        if (resultIndex !== -1) {
          closestGuessResults.value[resultIndex].bonusApplied = true;
        }
      }
    };

    const closeClosestGuessModal = () => {
      showClosestGuessModal.value = false;
      // Reset bonusApplied status when closing the modal without submitting
      closestGuessResults.value = closestGuessResults.value.map(result => ({...result, bonusApplied: false}));
    };

    const getTeamPointsThisRound = (team) => {
      if (!team) return 0;
      // Only show points after they've been manually saved
      if (team.submission?.submittedToFirestore) {
        return team.submission.totalScore || 0;
      }
      return 0;
    };

    const getQuestionType = (roundName, questionIndex) => {
      if (roundName === "Win - Place - Show" && questionIndex === 1) {
        return 'select';
      }
      return 'text'; // Default to text input for other questions
    };

    const getQuestionOptions = (roundName, questionIndex) => {
      if (roundName === "Win - Place - Show" && questionIndex === 1) {
        return [
          "Win - Your answer must be #1",
          "Place - Your answer can be #1 or #2",
          "Show - Your answer can be in the top 3"
        ];
      }
      return []; // Return empty array for other questions
    };

    const doNothing = (team) => {
      // Simulate scoring the round for the given team
      scoreRound(team);

      // Immediately close the scoring modal
      closeScoringModal();
    };

    // Make AUTO_SCORING_ROUNDS available to the template
    const autoScoringRounds = AUTO_SCORING_ROUNDS;

    const getScoreValue = (teamId, index) => {
      if (!teamId || !localScores.value[teamId]?.scores) return 0;
      return localScores.value[teamId].scores[index] || 0;
    };

    const updateScore = (event, teamId, index) => {
      if (!teamId) return;
      
      if (!localScores.value[teamId]) {
        const numAnswers = selectedTeam.value?.submission?.answers?.length || 0;
        localScores.value = {
          ...localScores.value,
          [teamId]: {
            scores: new Array(numAnswers).fill(0),
            autoScored: new Array(numAnswers).fill(false),
            totalScore: 0
          }
        };
      }

      const scores = [...(localScores.value[teamId].scores || [])];
      scores[index] = Number(event.target.value) || 0;
      
      localScores.value = {
        ...localScores.value,
        [teamId]: {
          ...localScores.value[teamId],
          scores,
          totalScore: scores.reduce((sum, score) => sum + (Number(score) || 0), 0)
        }
      };
    };

    const getAutoScored = (index) => {
      if (!selectedTeam.value?.id || !localScores.value[selectedTeam.value.id]) return false;
      return localScores.value[selectedTeam.value.id].autoScored?.[index] || false;
    };

    const getMaxPoints = (index) => {
      if (!currentRoundQuestions.value?.[index]) return 100;
      return currentRoundQuestions.value[index].pointValue || 100;
    };

    const getTotalScore = () => {
      if (!selectedTeam.value) return 0;
      
      const teamScores = localScores.value[selectedTeam.value.id];
      if (!teamScores) return 0;
      
      const total = teamScores.scores.reduce((sum, score) => sum + (score || 0), 0);
      console.log('Getting total score:', total);
      return total;
    };

    const getTeamScore = (index) => {
      if (!selectedTeam.value) return 0;
      
      const teamScores = localScores.value[selectedTeam.value.id];
      if (!teamScores) return 0;
      
      console.log('Getting score for index', index, ':', teamScores.scores[index]);
      return teamScores.scores[index];
    };

    const getTeamTotalScore = () => {
      if (!selectedTeam.value?.id || !localScores.value[selectedTeam.value.id]) return 0;
      return localScores.value[selectedTeam.value.id].totalScore || 0;
    };

    const updateTeamScore = (event, index) => {
      if (!selectedTeam.value?.id) return;
      
      const teamId = selectedTeam.value.id;
      if (!localScores.value[teamId]) {
        localScores.value[teamId] = {
          scores: new Array(selectedTeam.value.submission.answers.length).fill(0),
          autoScored: new Array(selectedTeam.value.submission.answers.length).fill(false),
          totalScore: 0
        };
      }

      const scores = [...localScores.value[teamId].scores];
      scores[index] = Number(event.target.value) || 0;
      
      localScores.value[teamId] = {
        ...localScores.value[teamId],
        scores,
        totalScore: scores.reduce((sum, score) => sum + (Number(score) || 0), 0)
      };
    };

    watch(
      () => props.activeRound,
      (newRound) => {
        if (newRound && newRound.id) {
          teams.value = [];
          selectedTeam.value = null;
          showScoringModal.value = false;
          localScores.value = {};
          teamSubmissionStatus.value = {}; // Reset submission status
          fetchSubmissions(newRound.id);
        } else {
          console.error('Invalid active round:', newRound);
        }
      },
      { immediate: true }
    );

    watch(
      () => localScores.value,
      (newScores) => {
        console.log('LocalScores updated:', newScores);
        // Update team scores when localScores changes
        // Note: Do NOT set hasBeenScored here - it should only be set when host clicks "Save Scores"
        teams.value = teams.value.map(team => ({
          ...team,
          submission: team.submission ? {
            ...team.submission,
            scores: newScores[team.id]?.scores || team.submission.scores,
            totalScore: newScores[team.id]?.totalScore || team.submission.totalScore
          } : team.submission
        }));
      },
      { deep: true }
    );

    watch(() => teams.value, (newTeams) => {
      if (!newTeams) return;
      
      // Recalculate submission status for all teams when teams change
      newTeams.forEach(team => {
        calculateTeamSubmissionStatus(team);
      });
      
      // Check each team for new submissions that need auto-scoring
      newTeams.forEach(team => {
        if (
          team.submission && 
          team.hasSubmitted && 
          !team.hasBeenScored && 
          !team.submission.preScored && 
          !team.submission.hasBeenAutoScored &&
          !pendingAutoScoring.value.has(team.id)
        ) {
          const roundName = selectedReviewRoundId.value 
            ? reviewRound.value?.roundName 
            : activeRound.value?.roundName;

          if (AUTO_SCORING_ROUNDS.includes(roundName)) {
            triggerAutoScoring(team);
          }
        }
      });
    }, { deep: true });

    onMounted(() => {
      fetchSubmissions(props.activeRound.id);
    });

    onUnmounted(() => {
      if (unsubscribeTeams) unsubscribeTeams();
    });

    return {
      selectedRoundId,
      selectedReviewRoundId,
      showRoundSelector,
      teams,
      selectedTeam,
      showScoringModal,
      showClosestGuessModal,
      closestGuessResults,
      changeActiveRound,
      reviewOtherRounds,
      reviewSelectedRound,
      closeRoundSelector,
      scoreRound,
      submitScores,
      closeScoringModal,
      findClosestPercentageGuess,
      closeClosestGuessModal,
      localScores,
      hasUnsavedScores,
      submitAllScores,
      submitAllScoresAndAdvance,
      applyBonusPoints,
      getTeamPointsThisRound,
      reviewRound,
      message,
      messageType,
      getQuestionType,
      getQuestionOptions,
      currentRoundQuestions,
      doNothing,
      isAutoScoring,
      pendingAutoScoring,
      autoScoringRounds,
      getScoreValue,
      updateScore,
      getAutoScored,
      getMaxPoints,
      getTotalScore,
      getTeamScore,
      getTeamTotalScore,
      updateTeamScore,
      teamSubmissionStatus,
      calculateTeamSubmissionStatus,
      getTeamSubmissionStatus,
    };
  },
};
</script>

<style>
.review-answers {
  position: relative;
}

.container {
  max-width: 800px;
}

.table th, .table td {
  vertical-align: middle;
}

/* Review Answers modals only — avoids breaking HostPage modals and fixes Chrome/Edge scroll */
.review-answers .modal.fade.show {
  position: fixed !important;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex !important;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  /* pointer-events: none on .modal breaks scrollbar / wheel on descendants in Chromium */
  pointer-events: auto;
  overflow-x: hidden;
  overflow-y: hidden;
}

.review-answers .modal-dialog {
  margin: 1.75rem auto;
}

.review-answers .modal-dialog.modal-dialog-scrollable {
  max-height: calc(100vh - 3.5rem);
}

.review-answers .modal-dialog-scrollable .modal-content {
  min-height: 0;
}

.review-answers .modal-dialog-scrollable .modal-body {
  min-height: 0;
}

.review-answers .modal-content {
  background-color: #fff;
  border-radius: 0.3rem;
  outline: 0;
  padding: 1rem;
  width: 100%;
}

/* Keep all other existing styles below this point */
.alert {
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 0.25rem;
}

.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.alert-danger {
  background-color: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
}

.auto-scored {
  background-color: rgba(0, 255, 0, 0.1);
}

.auto-scored.incorrect {
  background-color: transparent;
}

.table tr.auto-scored {
  background-color: rgba(0, 255, 0, 0.1);
}

.table tr.auto-scored:hover {
  background-color: rgba(0, 255, 0, 0.2);
}

tr[data-auto-scored="true"] {
  background-color: rgba(0, 255, 0, 0.1);
}

/* Base auto-scored style */
.modal-body .table tbody tr.auto-scored {
  background-color: rgba(0, 255, 0, 0.1);
}

/* Override Bootstrap's table-striped if present */
.modal-body .table-striped tbody tr.auto-scored:nth-of-type(odd),
.modal-body .table-striped tbody tr.auto-scored:nth-of-type(even) {
  background-color: rgba(0, 255, 0, 0.1);
}

/* Override hover state */
.modal-body .table tbody tr.auto-scored:hover {
  background-color: rgba(0, 255, 0, 0.2);
}

/* Additional specificity for edge cases */
.modal-body .table tbody tr[data-auto-scored="true"] {
  background-color: rgba(0, 255, 0, 0.1);
}

/* Ensure TD elements within auto-scored rows maintain the background */
.modal-body .table tbody tr.auto-scored td {
  background-color: rgba(0, 255, 0, 0.1);
}

/* Modal specific styles */
.review-answers .modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.review-answers .modal-body {
  position: relative;
  padding: 1rem;
}

.review-answers .modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 1rem;
  border-top: 1px solid #dee2e6;
  gap: 0.5rem;
}

.btn-close {
  padding: 0.5rem;
  margin: -0.5rem -0.5rem -0.5rem auto;
}

/* Auto-scoring highlight styles */
.auto-scored {
  background-color: rgba(0, 255, 0, 0.1);
}

.table tr.auto-scored {
  background-color: rgba(0, 255, 0, 0.1);
}

.table tr.auto-scored:hover {
  background-color: rgba(0, 255, 0, 0.2);
}

/* Make highlighting work in modal */
.modal-body .table tbody tr.auto-scored {
  background-color: rgba(0, 255, 0, 0.1);
}

.modal-body .table tbody tr.auto-scored td {
  background-color: rgba(0, 255, 0, 0.1);
}

/* Override any other table styles */
.modal-body .table-striped tbody tr.auto-scored:nth-of-type(odd),
.modal-body .table-striped tbody tr.auto-scored:nth-of-type(even) {
  background-color: rgba(0, 255, 0, 0.1);
}

.modal-body .table tbody tr.auto-scored:hover {
  background-color: rgba(0, 255, 0, 0.2);
}

.auto-scoring-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.robot-emoji {
  font-size: 1.2em;
  line-height: 1;
  display: inline-block;
}

.d-flex {
  display: flex;
  align-items: center;
}

.ms-2 {
  margin-left: 0.5rem !important;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.scoring-text {
  font-size: 1.2rem;
  color: #666;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

.gap-2 {
  gap: 0.5rem;
}

.review-answers .modal-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040; /* Ensure backdrop is below modal */
}

/* Submission status badge styles */
.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-weight: 500;
}

.bg-success {
  background-color: #28a745 !important;
  color: white !important;
}

.bg-warning {
  background-color: #ffc107 !important;
  color: #212529 !important;
}

.bg-danger {
  background-color: #dc3545 !important;
  color: white !important;
}

.ms-2 {
  margin-left: 0.5rem !important;
}
</style>
